import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import OwnerDashboardMenu from './OwnerDashboardMenu';

/**
 * ActiveUsersDashboard - OWNER ONLY dashboard for tracking live user activity
 * 
 * Shows:
 * - Who is logged in right now (active in last 15 mins)
 * - Allocations made in last hour / 24 hours
 * - Login days this week
 * - Session start time
 */

const ALLOWED_UIDS = [
  'mygxGSMYBWZd9v02Xyew6WMtYe13', // Anthony
  'uM4lKlr7SjPGFzDK064F1Dk6Aco2'  // Additional authorized user
];

const ActiveUsersDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [authorized, setAuthorized] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const navigate = useNavigate();

  // Check authorization
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user || !ALLOWED_UIDS.includes(user.uid)) {
        navigate('/');
        return;
      }
      setAuthorized(true);
      setAuthChecked(true);
    });
    return () => unsubscribe();
  }, [navigate]);

  const fetchMetrics = async () => {
    setLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const getActiveUsersMetrics = httpsCallable(functions, 'getActiveUsersMetrics');
      const result = await getActiveUsersMetrics();
      setMetrics(result.data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error fetching active users metrics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authorized) {
      fetchMetrics();
    }
  }, [authorized]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!authorized || !autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchMetrics();
    }, 30000);

    return () => clearInterval(interval);
  }, [authorized, autoRefresh]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '—';
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp._seconds) {
      date = new Date(timestamp._seconds * 1000);
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    
    if (isNaN(date.getTime())) return '—';
    
    return date.toLocaleTimeString('en-AU', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '—';
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp._seconds) {
      date = new Date(timestamp._seconds * 1000);
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    
    if (isNaN(date.getTime())) return '—';
    
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    return formatTime(timestamp);
  };

  const getStatusColor = (lastActivity) => {
    if (!lastActivity) return '#9ca3af';
    
    let date;
    if (lastActivity._seconds) {
      date = new Date(lastActivity._seconds * 1000);
    } else if (lastActivity.seconds) {
      date = new Date(lastActivity.seconds * 1000);
    } else {
      date = new Date(lastActivity);
    }
    
    const diffMins = (Date.now() - date.getTime()) / 60000;
    
    if (diffMins <= 5) return '#22c55e';  // Green - active now
    if (diffMins <= 15) return '#f59e0b'; // Yellow - recently active
    if (diffMins <= 60) return '#94a3b8'; // Gray - idle
    return '#e5e7eb'; // Light gray - offline
  };

  const getStatusLabel = (lastActivity) => {
    if (!lastActivity) return 'Offline';
    
    let date;
    if (lastActivity._seconds) {
      date = new Date(lastActivity._seconds * 1000);
    } else if (lastActivity.seconds) {
      date = new Date(lastActivity.seconds * 1000);
    } else {
      date = new Date(lastActivity);
    }
    
    const diffMins = (Date.now() - date.getTime()) / 60000;
    
    if (diffMins <= 5) return 'Online';
    if (diffMins <= 15) return 'Active';
    if (diffMins <= 60) return 'Idle';
    return 'Offline';
  };

  if (!authChecked) {
    return (
      <div style={styles.loadingContainer}>
        <p style={styles.loadingText}>Loading...</p>
      </div>
    );
  }

  if (!authorized) return null;

  return (
    <div style={styles.pageContainer}>
      <OwnerDashboardMenu />
      
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Active Users Dashboard</h1>
            <p style={styles.subtitle}>
              Real-time user activity and engagement
            </p>
          </div>
          <div style={styles.headerActions}>
            <label style={styles.autoRefreshLabel}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                style={styles.checkbox}
              />
              Auto-refresh
            </label>
            <span style={styles.lastRefresh}>
              Updated: {lastRefresh?.toLocaleTimeString()}
            </span>
            <button style={styles.refreshButton} onClick={fetchMetrics} disabled={loading}>
              {loading ? '↻ Loading...' : '↻ Refresh'}
            </button>
          </div>
        </div>

        {error && (
          <div style={styles.errorBanner}>
            ⚠️ Error: {error}
          </div>
        )}

        {/* Summary Cards */}
        <div style={styles.summaryGrid}>
          <div style={styles.summaryCard}>
            <div style={styles.summaryIcon}>🟢</div>
            <div style={styles.summaryValue}>{metrics?.summary?.onlineNow || 0}</div>
            <div style={styles.summaryLabel}>Online Now</div>
            <div style={styles.summarySubtext}>Last 5 minutes</div>
          </div>
          
          <div style={styles.summaryCard}>
            <div style={styles.summaryIcon}>⚡</div>
            <div style={styles.summaryValue}>{metrics?.summary?.activeToday || 0}</div>
            <div style={styles.summaryLabel}>Active Today</div>
            <div style={styles.summarySubtext}>Logged in today</div>
          </div>
          
          <div style={styles.summaryCard}>
            <div style={styles.summaryIcon}>📝</div>
            <div style={styles.summaryValue}>{metrics?.summary?.allocationsLastHour || 0}</div>
            <div style={styles.summaryLabel}>Allocations (1h)</div>
            <div style={styles.summarySubtext}>Last hour</div>
          </div>
          
          <div style={styles.summaryCard}>
            <div style={styles.summaryIcon}>📊</div>
            <div style={styles.summaryValue}>{metrics?.summary?.allocationsLast24h || 0}</div>
            <div style={styles.summaryLabel}>Allocations (24h)</div>
            <div style={styles.summarySubtext}>Last 24 hours</div>
          </div>
        </div>

        {/* Active Users Table */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            👥 User Activity ({metrics?.users?.length || 0})
          </h2>
          
          {loading && !metrics ? (
            <div style={styles.loadingInline}>Loading users...</div>
          ) : metrics?.users?.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>😴</div>
              <p>No active users in the last 24 hours</p>
            </div>
          ) : (
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>User</th>
                    <th style={styles.th}>Club</th>
                    <th style={styles.th}>Session Start</th>
                    <th style={styles.th}>Last Activity</th>
                    <th style={styles.thCenter}>Allocs (1h)</th>
                    <th style={styles.thCenter}>Allocs (24h)</th>
                    <th style={styles.thCenter}>Days This Week</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics?.users?.map((user, index) => (
                    <tr key={user.id || index} style={styles.tr}>
                      <td style={styles.td}>
                        <div style={styles.statusContainer}>
                          <div 
                            style={{
                              ...styles.statusDot,
                              backgroundColor: getStatusColor(user.lastActivity)
                            }}
                          />
                          <span style={styles.statusText}>
                            {getStatusLabel(user.lastActivity)}
                          </span>
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div style={styles.userInfo}>
                          <div style={styles.userName}>
                            {user.firstName && user.lastName 
                              ? `${user.firstName} ${user.lastName}`
                              : user.email?.split('@')[0] || 'Unknown'}
                          </div>
                          <div style={styles.userEmail}>{user.email}</div>
                        </div>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.clubBadge}>{user.clubName || user.clubId}</span>
                      </td>
                      <td style={styles.td}>
                        {formatTime(user.sessionStart)}
                      </td>
                      <td style={styles.td}>
                        {formatTimeAgo(user.lastActivity)}
                      </td>
                      <td style={styles.tdCenter}>
                        <span style={{
                          ...styles.countBadge,
                          backgroundColor: user.allocationsLastHour > 0 ? '#dcfce7' : '#f3f4f6',
                          color: user.allocationsLastHour > 0 ? '#166534' : '#6b7280'
                        }}>
                          {user.allocationsLastHour || 0}
                        </span>
                      </td>
                      <td style={styles.tdCenter}>
                        <span style={{
                          ...styles.countBadge,
                          backgroundColor: user.allocationsLast24h > 0 ? '#dbeafe' : '#f3f4f6',
                          color: user.allocationsLast24h > 0 ? '#1e40af' : '#6b7280'
                        }}>
                          {user.allocationsLast24h || 0}
                        </span>
                      </td>
                      <td style={styles.tdCenter}>
                        <div style={styles.weekDots}>
                          {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, i) => (
                            <div
                              key={i}
                              title={['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i]}
                              style={{
                                ...styles.weekDot,
                                backgroundColor: user.loginDaysThisWeek?.includes(i) 
                                  ? '#22c55e' 
                                  : '#e5e7eb'
                              }}
                            />
                          ))}
                        </div>
                        <div style={styles.daysCount}>
                          {user.loginDaysThisWeek?.length || 0}/7 days
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  pageContainer: {
    minHeight: '100vh',
    backgroundColor: '#f1f5f9',
  },
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '24px',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
  },
  loadingText: {
    fontSize: '18px',
    color: '#6b7280',
  },
  loadingInline: {
    padding: '40px',
    textAlign: 'center',
    color: '#6b7280',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#243665',
    margin: '0 0 4px 0',
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  autoRefreshLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#64748b',
    cursor: 'pointer',
  },
  checkbox: {
    cursor: 'pointer',
  },
  lastRefresh: {
    fontSize: '12px',
    color: '#94a3b8',
  },
  refreshButton: {
    padding: '8px 16px',
    backgroundColor: '#243665',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  },
  errorBanner: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#dc2626',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '24px',
    fontSize: '14px',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  summaryCard: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    textAlign: 'center',
  },
  summaryIcon: {
    fontSize: '24px',
    marginBottom: '8px',
  },
  summaryValue: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#243665',
    marginBottom: '4px',
  },
  summaryLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
  },
  summarySubtext: {
    fontSize: '11px',
    color: '#94a3b8',
    marginTop: '2px',
  },
  section: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    overflow: 'hidden',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b',
    margin: 0,
    padding: '20px 24px',
    borderBottom: '1px solid #e5e7eb',
  },
  tableContainer: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '12px 16px',
    fontSize: '12px',
    fontWeight: '600',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e5e7eb',
  },
  thCenter: {
    textAlign: 'center',
    padding: '12px 16px',
    fontSize: '12px',
    fontWeight: '600',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e5e7eb',
  },
  tr: {
    borderBottom: '1px solid #f1f5f9',
  },
  td: {
    padding: '14px 16px',
    fontSize: '14px',
    color: '#374151',
    verticalAlign: 'middle',
  },
  tdCenter: {
    padding: '14px 16px',
    fontSize: '14px',
    color: '#374151',
    textAlign: 'center',
    verticalAlign: 'middle',
  },
  statusContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  statusDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
  },
  statusText: {
    fontSize: '12px',
    fontWeight: '500',
    color: '#64748b',
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  userName: {
    fontWeight: '500',
    color: '#1e293b',
  },
  userEmail: {
    fontSize: '12px',
    color: '#94a3b8',
  },
  clubBadge: {
    fontSize: '12px',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    padding: '4px 8px',
    borderRadius: '4px',
    fontWeight: '500',
  },
  countBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: '600',
  },
  weekDots: {
    display: 'flex',
    gap: '3px',
    justifyContent: 'center',
    marginBottom: '4px',
  },
  weekDot: {
    width: '12px',
    height: '12px',
    borderRadius: '3px',
  },
  daysCount: {
    fontSize: '11px',
    color: '#94a3b8',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '12px',
  },
};

export default ActiveUsersDashboard;
